import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, Trash2, BookOpen, Sparkles, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/** AI Interview OS: AI Knowledge Studio. Recruiters upload their OWN company-specific interview
 * question bank / guide (PDF/DOCX/TXT) — AI extracts the topics and question style, so
 * candidates you invite to interview get genuinely company-specific questions instead of the
 * generic domain question bank. */
export default function KnowledgeStudioPanel() {
  const [kbs, setKbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPasteForm, setShowPasteForm] = useState(false);
  const [pasteName, setPasteName] = useState("");
  const [pasteText, setPasteText] = useState("");
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/recruiter/knowledge-bases").then((r) => setKbs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createFromText = async (name, text) => {
    setUploading(true);
    try {
      const { data } = await api.post("/recruiter/knowledge-bases", { name, text });
      setKbs((prev) => [data, ...prev]);
      toast.success(`"${data.name}" is ready — attach it next time you invite a candidate to interview.`);
      setShowPasteForm(false);
      setPasteName("");
      setPasteText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build a knowledge base from this");
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: extracted } = await api.post("/resume-checker/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await createFromText(file.name.replace(/\.[^/.]+$/, ""), extracted.resume_text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read this file — try a different one");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async (kbId) => {
    try {
      await api.delete(`/recruiter/knowledge-bases/${kbId}`);
      setKbs((prev) => prev.filter((k) => k.id !== kbId));
      toast.success("Knowledge base deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="recruiter-knowledge-studio-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <p className="text-sm text-slate-400 max-w-xl">
          Upload your own interview question bank, guide, or rubric (PDF/DOCX/TXT) — AI extracts the topics and question
          style, so candidates you invite to interview get genuinely <span className="text-white font-semibold">company-specific</span> questions.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowPasteForm(true)} data-testid="recruiter-kb-paste-text-btn" className="text-xs font-semibold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg">
            Paste Text Instead
          </button>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="recruiter-kb-upload-btn" className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-xs font-semibold px-3.5 py-2 rounded-lg">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Upload Document
          </button>
          <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="recruiter-kb-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto my-10" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="recruiter-kb-list">
          {kbs.map((kb) => (
            <div key={kb.id} className="neu-card rounded-xl p-4" data-testid={`recruiter-kb-item-${kb.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-white flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-indigo-300 shrink-0" /> {kb.name}</p>
                <button onClick={() => remove(kb.id)} data-testid={`recruiter-kb-delete-${kb.id}`} className="text-slate-400 hover:text-rose-400 p-1 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">{kb.summary}</p>
              <div className="flex items-center gap-1.5 flex-wrap mt-3">
                {(kb.topics || []).map((t) => (
                  <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">{t}</span>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">{(kb.sample_questions || []).length} sample question(s) extracted</p>
            </div>
          ))}
          {kbs.length === 0 && (
            <p className="sm:col-span-2 text-sm text-slate-400 text-center py-10 flex flex-col items-center gap-2" data-testid="recruiter-kb-empty-state">
              <BookOpen className="w-6 h-6 text-slate-400" /> No knowledge bases yet — upload your first interview guide above.
            </p>
          )}
        </div>
      )}

      {showPasteForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="recruiter-kb-paste-modal">
          <div className="glass-panel rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Paste Your Question Bank</h3>
              <button onClick={() => setShowPasteForm(false)} data-testid="recruiter-kb-paste-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Knowledge Base Name *</label>
                <input value={pasteName} onChange={(e) => setPasteName(e.target.value)} placeholder="e.g. Backend Engineer Round 2" data-testid="recruiter-kb-paste-name-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Question Bank / Guide Text *</label>
                <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={8} placeholder="Paste your interview questions, rubric, or guide here..." data-testid="recruiter-kb-paste-text-area" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none" />
              </div>
            </div>
            <button
              onClick={() => createFromText(pasteName.trim(), pasteText.trim())}
              disabled={uploading || !pasteName.trim() || pasteText.trim().length < 50}
              data-testid="recruiter-kb-paste-save-btn"
              className="w-full mt-5 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Build Knowledge Base
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
